import { ISearchResult, IResults, IErrorType } from '../state-helpers/search-results';
import { IFacetResult } from '../state-helpers/facets';
import { urlReplaceInAction, urlPushAction, UrlQueryParamTypes, encode } from 'react-url-query';
import { IEpiProps } from '../state-helpers/epi-props';
import { fetchResults, FetchResultsFlags } from './results';
import { IAutocompleteResults, IHit } from '../state-helpers/autocomplete-results';
import { GlobalActionType } from '../../global/actions/index';
import * as Rx from 'rx';
import * as _ from 'lodash';
import { ISearchContext } from '../../global/models/search-context';
import { ISort } from '../state-helpers/sorts'
import { ErrorConstants } from '../../global/constants/constants';
export const CHANGE_QUERY = 'CHANGE_QUERY';

export const changeSearchTerm = urlReplaceInAction(CHANGE_QUERY, 'q', UrlQueryParamTypes.string);

// Enums
export class ActionType extends GlobalActionType {
    static SET_PAGE: string = "SET_PAGE";
    static SET_PAGE_TITLE: string = "SET_PAGE_TITLE";
    static AUTOCOMPLETE_RESULTS_LOADED: string = "AUTOCOMPLETE_RESULTS_LOADED";
    static AUTOCOMPLETE_ACTIVE_HIT_CHANGE: string = "AUTOCOMPLETE_ACTIVE_HIT_CHANGE";
    static AUTOCOMPLETE_ACTIVE_CHANGED: string = "AUTOCOMPLETE_ACTIVE_CHANGED";
    static SET_SELECTED_FACET_COUNT: string = "SET_SELECTED_FACET_COUNT";
    static RESULTS_LOADED: string = "RESULTS_LOADED";
    static RESULTS_LOADING: string = "RESULTS_LOADING";
    static RESULTS_LOAD_FAILURE: string = "RESULTS_LOAD_FAILURE";
    static SHOW_RESULTS: string = "SHOW_RESULTS";
    static SET_SORT: string = "SET_SORT";
    static SET_SORT_MODEL: string = "SET_SORT_MODEL";
    static SET_CONTEXT: string = "SET_CONTEXT";
    static SET_SEARCH_TERM: string = "SET_SEARCH_TERM";
    static SET_MOBILE_FACETS_VISIBILITY: string = "SET_MOBILE_FACETS_VISIBILITY";
    static SET_FACET_DROPDOWN_VISIBILITY: string = "SET_FACET_DROPDOWN_VISIBILITY";
    static UPDATE_FACETS: string = "UPDATE_FACETS";
    static TOGGLE_MOBILE_NAV: string = "TOGGLE_MOBILE_NAV";
    static SET_EPI_PROPS: string = "SET_EPI_PROPS";
    static EPI_PROPS_LOADED: string = "EPI_PROPS_LOADED";
    static BROWSER_NAVIGATION: string = "BROWSER_NAVIGATION";
    static UPDATE_QUERY: string = "UPDATE_QUERY";
    static CLEAR_ERROR_TEXT: string = "CLEAR_ERROR_TEXT";
}

export enum ActionOrigin {
    BROWSER_NAVIGATION
}


// Interfaces
export interface IAction {
    type: ActionType;
    origin?: ActionOrigin;
}

export interface IResultsLoadedAction extends IAction {
    results: ISearchResult[];
    facets: IFacetResult[];
    page: number;
    errorMessage: string;
    errorType: IErrorType;
    totalNumber: number;
    totalPages: number;
    trackingId: string;
    showResults?: boolean;
    isLoading?: boolean;
    suggestion: string;
    fromAutocomplete: boolean;  // If the search term was from an autocomplete suggestion
}

export interface IAutocompleteResultsLoadedAction extends IAction {
    hits: IHit[];
    activeHit: number;
	isActive: boolean;
}

export interface IAutocompleteSetActive extends IAction {
	isActive: boolean;
}

export interface ISetSelectedFacetCount extends IAction {
    selectedFacetCount: number;
}

export interface IAutocompleteActiveHitChange extends IAction {
    activeHit: number;
}

export interface IEpiPropsLoadedAction extends IAction {
    results: IEpiProps;
}

export function getEpiProps(results: IEpiProps): IEpiPropsLoadedAction {
    return {
        type: ActionType.EPI_PROPS_LOADED,
        results: results
    }
}

export interface ISearchIsLoading extends IAction {
    isLoading: boolean;
}

export interface IShowResults extends IAction {
    showResults: boolean;
}

export interface ISetSortAction extends IAction {
    sortId: string;
    sortModel: ISort[];
}

export interface IUpdateSearchFacet extends IAction {
    facets: IFacetResult[]
}

export interface ISetPageAction extends IAction {
    page: number;
}

export interface ISetSearchTermAction extends IAction {
    searchTerm: string;
}

export interface ISetContextAction extends IAction {
    context: ISearchContext;
}

export interface ISetMobileFacetsVisibility extends IAction {
    visible: boolean;
}

export interface ISetFacetDropdownVisibility extends IAction {
    visible: boolean;
}

// Actions
export function itemsAreLoading(isLoading: boolean): ISearchIsLoading {
    return {
        type: ActionType.RESULTS_LOADING,
        isLoading: isLoading
    }
}

export function showResults(showResults: boolean): IShowResults {
    return {
        type: ActionType.SHOW_RESULTS,
        showResults: showResults
    }
}

export function noResults(): IResultsLoadedAction {
    return {
        type: ActionType.SET_PAGE,
        facets: [],
        results: [],
        page: 1,
        errorMessage: '',
        totalNumber: 0,
        totalPages: 0,
        trackingId: '',
        suggestion: '',
        errorType: IErrorType.NONE,
        fromAutocomplete: false
    }
}

export function getResults(results: IResults, fromAutocomplete: boolean = false): IResultsLoadedAction {
    return {
        type: ActionType.RESULTS_LOADED,
        results: results.searchItemList,
        facets: results.facets,
        page: results.currentPage,
        errorMessage: '',
        totalNumber: results.totalNumber,
        totalPages: results.totalPages,
        trackingId: results.trackingId,
        suggestion: results.suggestion,
        errorType: IErrorType.NONE,
        fromAutocomplete: fromAutocomplete
    }
}

export function setAutocompleteActive(isActive: boolean): IAutocompleteSetActive {
    return {
        type: ActionType.AUTOCOMPLETE_ACTIVE_CHANGED,
		isActive
    }
}

export function getAutocompleteResults(results: IAutocompleteResults): IAutocompleteResultsLoadedAction {
    return {
        type: ActionType.AUTOCOMPLETE_RESULTS_LOADED,
        hits: results != null ? results.hits : [] as IHit[],
        activeHit: 0,
		isActive: true
    }
}

export function activeHitChange(activeHit: number): IAutocompleteActiveHitChange {
    return {
        type: ActionType.AUTOCOMPLETE_ACTIVE_HIT_CHANGE,
        activeHit
    }
}
export function clearErrorText(results: IResults): IResultsLoadedAction {
    return {
        type: ActionType.CLEAR_ERROR_TEXT,
        results: results.searchItemList,
        facets: results.facets,
        page: results.currentPage,
        errorMessage: '',
        totalNumber: results.totalNumber,
        totalPages: results.totalPages,
        errorType: IErrorType.NONE,
        trackingId: results.trackingId,
        suggestion: results.suggestion,
        fromAutocomplete: false
    }
}


export function updateResultsFailure(errorMessage: string): IResultsLoadedAction {
    var errorType;
    switch (errorMessage) {
        case ErrorConstants.BadReponseMessage:
            errorType = IErrorType.GENERAL;
            break;
        case ErrorConstants.FailedToFetchMessage:
            errorType = IErrorType.TIME_OUT;
            break;
        default:
            errorType = IErrorType.NONE;
            break;
    }

    return {
        type: ActionType.RESULTS_LOAD_FAILURE,
        errorMessage: errorMessage,
        results: [],
        facets: [],
        page: 1,
        totalNumber: 0,
        totalPages: 0,
        errorType: errorType,
        trackingId: '',
        suggestion: '',
        fromAutocomplete: false
    }
}

export function setSort(sortId: string): ISetSortAction {
    return {
        type: ActionType.SET_SORT,
        sortId,
        sortModel: null
    };
}

export function setSortModel(sortModel: ISort[]): ISetSortAction {
    return {
        type: ActionType.SET_SORT_MODEL,
        sortId: null,
        sortModel: sortModel
    };
}

export function setPage(page: number): ISetPageAction {
    return {
        type: ActionType.SET_PAGE,
        page
    };
}

export interface ISearchTextUpdatedAction extends IAction {
    searchText: string;
}

export interface ISetFiltersAction extends IAction {
    filters: { id: string, selectedOptionIds: string[] }[];
}

export interface IBrowserAction extends IAction, ISearchTextUpdatedAction, ISetSearchTermAction,  ISetSortAction, /*ISetFiltersAction,*/ ISetPageAction, ISetMobileFacetsVisibility, ISetFacetDropdownVisibility { }

export function setSearchTerm(searchTerm: string): ISetSearchTermAction {
    return {
        type: ActionType.SET_SEARCH_TERM,
        searchTerm
    };
}

export function setMobileFacetsVisibility(visible: boolean): ISetMobileFacetsVisibility {
    return {
        type: ActionType.SET_MOBILE_FACETS_VISIBILITY,
        visible
    };
}

export function setFacetDropdownVisibility(visible: boolean): ISetFacetDropdownVisibility {
    return {
        type: ActionType.SET_FACET_DROPDOWN_VISIBILITY,
        visible
    };
}

export function setSelectedFacetCount(selectedFacetCount: number): ISetSelectedFacetCount {
    return {
        type: ActionType.SET_SELECTED_FACET_COUNT,
        selectedFacetCount
    };
}

export function setActiveHit(activeHit: number): IAutocompleteActiveHitChange {
    return {
        type: ActionType.AUTOCOMPLETE_ACTIVE_HIT_CHANGE,
        activeHit
    };
}


export function setContext(context: ISearchContext): ISetContextAction {
    return {
        type: ActionType.SET_CONTEXT,
        context
    };
}

export function setPageTitle(searchTerm: string, contextId: string) : IAction {
    return {
        type: ActionType.SET_PAGE_TITLE
    };
}

export function browser(action: IAction) {
    return _.assign(action, { origin: ActionOrigin.BROWSER_NAVIGATION }) as IAction;
}

export function updateResults(results: IResults, page = 1): IResultsLoadedAction {
    return {
        type: ActionType.RESULTS_LOADED,
        results: results.searchItemList,
        facets: results.facets,
        page: results.currentPage,
        errorMessage: results.errorMessage,
        errorType: results.errorType,
        totalNumber: results.totalNumber,
        totalPages: results.totalPages,
        isLoading: results.loading,
        trackingId: results.trackingId,
        suggestion: results.suggestion,
        fromAutocomplete: false
    };
}

export function updateSearchFacet(facets: IFacetResult[]): IUpdateSearchFacet {
    return {
        type: ActionType.UPDATE_FACETS,
		facets
    }

}